// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** English UI strings: The vault workspace: panels, tree, editor, search (workspace/ui, second half). Merged into the catalog by messages.en.ts. */
export const panels = {
  "panels.workspace.goneFromList": "This vault is no longer on the list.",
  "panels.workspace.backToLocal": "Back to local vaults",
  "panels.workspace.vault": "Vault",
  "panels.workspace.loading": "Loading vault…",
  "panels.workspace.firstSync": "Syncing {name}…",
  "panels.workspace.firstSyncVault": "Syncing your vault…",
  "panels.workspace.firstSyncHint": "Your notes appear as soon as they arrive.",
  "panels.workspace.breadcrumbs": "Location",
  "panels.workspace.newNote": "New note",
  "panels.workspace.tools": "Tools",
  "panels.workspace.cli": "Command line tool",
  "panels.workspace.open": "Open…",
  "panels.workspace.commands": "Commands",
  "panels.workspace.notes": "Notes",
  "panels.workspace.offline": "Offline: changes are kept and will sync",
  "panels.workspace.signedOut": "Sign in to sync",
  "panels.workspace.signedOutHint": "You are signed out. Changes are kept on this computer and sync after you sign in.",
  "panels.workspace.syncing": "Syncing…",
  "panels.workspace.synced": "All changes synced",
  "panels.workspace.left": "Files and tags",
  "panels.workspace.right": "Note details",
  "panels.workspace.noteOptions": "Note options",
  "panels.workspace.share": "Share",
  "panels.tab.files": "Files",
  "panels.tab.search": "Search",
  "panels.tab.tags": "Tags",
  "panels.tab.backlinks": "Backlinks",
  "panels.tab.outline": "Outline",
  "panels.tab.graph": "Graph",
  "panels.tab.related": "Related",
  "panels.tab.comments": "Comments",

  "panels.tree.empty": "This vault is empty.",
  "panels.tree.import": "Import notes…",
  "panels.tree.label": "Files",
  "panels.tree.newNoteInFolder": "New note in folder",
  "panels.tree.newFolder": "New folder",
  "panels.tree.moveTo": "Move to “{folder}”",
  "panels.tree.moveToTop": "Move to the top level",
  "panels.pane.markdownMode": "How Markdown shows",
  "panels.pane.styled": "Styled: formatting shows as it will look",
  "panels.pane.raw": "Raw Markdown: show the ** and # marks",
  "panels.tree.newFolderInFolder": "New folder inside",
  "panels.tree.rename": "Rename…",
  "panels.tree.duplicate": "Duplicate",
  "panels.tree.renameKey": "F2",
  "panels.tree.delete": "Delete…",
  "panels.tree.deleteKey": "Del",
  "panels.tree.history": "Version history…",
  "panels.tree.share": "Share…",

  "panels.graph.settings": "Graph settings",
  "panels.graph.filter": "Filter graph",
  "panels.graph.filterPlaceholder": "Filter (words, path:, tag:, file:)",
  "panels.graph.tags": "Tags",
  "panels.graph.attachments": "Attachments",
  "panels.graph.missing": "Links to missing notes",
  "panels.graph.orphans": "Notes without links",
  "panels.graph.depth": "Depth",
  "panels.graph.depthValue.one": "{count} link",
  "panels.graph.depthValue.other": "{count} links",
  "panels.graph.colorGroups": "Color groups",
  "panels.graph.groupColor": "Group {n} color",
  "panels.graph.groupQuery": "Group {n} query",
  "panels.graph.groupPlaceholder": "path:Projects or tag:work",
  "panels.graph.removeGroup": "Remove group {n}",
  "panels.graph.addGroup": "Add group",
  "panels.graph.summary.one":
    "Graph of {count} note and {links} links. Use the file list, backlinks, or quick switcher to move between notes with the keyboard.",
  "panels.graph.summary.other":
    "Graph of {count} notes and {links} links. Use the file list, backlinks, or quick switcher to move between notes with the keyboard.",

  "panels.editor.placeholder": "Start writing…",
  "panels.editor.embedNotFound": "{target} (not found)",
  "panels.editor.label": "Note {title}",
  "panels.editor.live": "Live editing",
  "panels.editor.liveBadge": "Live",
  "panels.editor.connecting": "Connecting…",
  "panels.editor.reconnecting": "Reconnecting…",
  "panels.editor.readOnly": "Read only",
  "panels.editor.people": "People in this note",
  "panels.editor.follow": "Follow {name}",
  "panels.editor.stopFollowing": "Stop following {name}",
  "panels.editor.following": "Following {name}",
  "panels.editor.cannotShow": "Granite cannot show this kind of file yet.",

  "panels.pane.graphView": "Graph view",
  "panels.pane.label": "Pane {n}",
  "panels.pane.openNotes": "Open notes",
  "panels.pane.closeTab": "Close {title}",
  "panels.pane.splitRight": "Split right",
  "panels.pane.splitRightTitle": "Split right ({shortcut})",
  "panels.pane.closePane": "Close pane",
  "panels.pane.empty": "No note is open.",
  "panels.vaultSwitcher.label": "Vault: {name}. Switch vault",
  "panels.vaultSwitcher.local": "On this computer",
  "panels.vaultSwitcher.all": "All vaults",
  "panels.dialog.close": "Close",
  "panels.command.toggle-line-numbers": "Toggle line numbers",
  "panels.workspace.dailyNote": "Today's daily note",
  "panels.command.new-from-template": "New note from template…",
  "panels.command.save-as-template": "Save as template",
  "panels.workspace.duplicate": "Duplicate",
  "panels.tree.newFromTemplateInFolder": "New note from template…",
  "panels.templates.newTitle": "New note from template",
  "panels.templates.empty": "No templates yet. Save a note as a template from its ⋯ menu.",
  "panels.templates.saved": "Saved as the template “{name}”.",
  "panels.pane.newNote": "New note",
  "panels.pane.openNote": "Open a note ({shortcut})",
  "panels.pane.noteTitle": "Note title",

  "panels.search.label": "Search notes",
  "panels.search.mode": "Search mode",
  "panels.search.exact": "Exact",
  "panels.search.semantic": "Semantic",
  "panels.search.syntax": "Words, \"phrases\", -exclude, tag:, path:, file:",
  "panels.search.noNotes": "No notes match.",
  "panels.search.notes.one": "{count} note",
  "panels.search.notes.other": "{count} notes",
  "panels.search.results": "Search results",
  "panels.search.semanticIntro": "Finds passages by meaning in this synced vault. Changes appear within a minute.",
  "panels.search.noPassages": "No passages match.",
  "panels.search.passages.one": "{count} passage",
  "panels.search.passages.other": "{count} passages",
  "panels.search.indexing.one": "(indexing {count} file…)",
  "panels.search.indexing.other": "(indexing {count} files…)",
  "panels.search.semanticResults": "Semantic search results",
  "panels.search.page": "Page {page}",

  "panels.backlinks.noNote": "Open a note to see what links to it.",
  "panels.backlinks.none": "No notes link here yet.",
  "panels.backlinks.label": "Backlinks",
  "panels.outline.noNote": "Open a note to see its outline.",
  "panels.outline.none": "This note has no headings.",
  "panels.outline.label": "Outline",
  "panels.outline.emptyHeading": "(empty heading)",
  "panels.tags.filter": "Filter tags",
  "panels.tags.none": "No tags yet. Type #tag in a note.",
  "panels.tags.noMatch": "No matching tags.",
  "panels.tags.label": "Tags",
  "panels.tags.notesTagged": "Notes tagged {tag}",
  "panels.related.needsSync": "Related notes need a synced vault.",
  "panels.related.noNote": "Open a note to see related notes.",
  "panels.related.looking": "Looking for related notes…",
  "panels.related.none": "No related notes yet.",
  "panels.related.label": "Related notes",
} as const;
